import { type FC, useEffect, useState, type FormEvent } from 'react';
import workspacesApi from '@/api/workspaces.api';
import type { WorkspaceMember } from '@/types/workspace.types';
import { Role } from '@/types/user.types';
import { useAuthStore } from '@/stores/auth.store';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';

interface MembersPanelProps {
  workspaceId: string;
}

const roleBadgeColors: Record<Role, { bg: string; text: string }> = {
  [Role.Admin]: { bg: '#7c3aed22', text: '#7c3aed' },
  [Role.Editor]: { bg: '#2563eb22', text: '#2563eb' },
  [Role.Reader]: { bg: '#6b728022', text: '#6b7280' },
};

const roleLabels: Record<Role, string> = {
  [Role.Admin]: 'Admin',
  [Role.Editor]: 'Editeur',
  [Role.Reader]: 'Lecteur',
};

export const MembersPanel: FC<MembersPanelProps> = ({ workspaceId }) => {
  const currentUser = useAuthStore((s) => s.user);
  const [members, setMembers] = useState<WorkspaceMember[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Invite form state
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>(Role.Editor);
  const [isInviting, setIsInviting] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);

  const currentUserRole = members.find((m) => m.userId === currentUser?.id)?.role;
  const isAdmin = currentUserRole === Role.Admin;

  useEffect(() => {
    loadMembers();
  }, [workspaceId]);

  const loadMembers = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await workspacesApi.getMembers(workspaceId);
      setMembers(data);
    } catch {
      setError('Impossible de charger les membres.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInvite = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setIsInviting(true);
    setInviteError(null);
    try {
      const newMember = await workspacesApi.inviteMember(workspaceId, {
        email: email.trim(),
        role,
      });
      setMembers((prev) => [...prev, newMember]);
      setEmail('');
      setRole(Role.Editor);
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Erreur lors de l'invitation.";
      setInviteError(message);
    } finally {
      setIsInviting(false);
    }
  };

  const handleRemove = async (userId: string) => {
    try {
      await workspacesApi.removeMember(workspaceId, userId);
      setMembers((prev) => prev.filter((m) => m.userId !== userId));
    } catch {
      // Silently ignore or could add error handling
    }
  };

  if (isLoading) {
    return (
      <p style={{ color: 'var(--color-text-muted)', padding: 'var(--spacing-md)' }}>
        Chargement des membres...
      </p>
    );
  }

  if (error) {
    return (
      <p style={{ color: 'var(--color-danger)', padding: 'var(--spacing-md)' }}>
        {error}
      </p>
    );
  }

  return (
    <div
      style={{
        padding: 'var(--spacing-lg)',
        backgroundColor: 'var(--color-surface)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-lg)',
      }}
    >
      <h2
        style={{
          fontSize: 'var(--font-size-lg)',
          fontWeight: 600,
          marginBottom: 'var(--spacing-md)',
          color: 'var(--color-text)',
        }}
      >
        Membres
      </h2>

      {/* Members list */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--spacing-sm)',
          marginBottom: 'var(--spacing-lg)',
        }}
      >
        {members.map((member) => (
          <div
            key={member.userId}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: 'var(--spacing-sm) var(--spacing-md)',
              borderRadius: 'var(--radius-md)',
              backgroundColor: 'var(--color-background)',
              border: '1px solid var(--color-border)',
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <span
                style={{
                  fontSize: 'var(--font-size-md)',
                  fontWeight: 500,
                  color: 'var(--color-text)',
                }}
              >
                {member.firstName} {member.lastName}
              </span>
              <span
                style={{
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-text-muted)',
                }}
              >
                {member.email}
              </span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
              <span
                style={{
                  display: 'inline-block',
                  padding: '2px 10px',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-xs)',
                  fontWeight: 600,
                  backgroundColor: roleBadgeColors[member.role].bg,
                  color: roleBadgeColors[member.role].text,
                }}
              >
                {roleLabels[member.role]}
              </span>

              {isAdmin && member.userId !== currentUser?.id && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRemove(member.userId)}
                  style={{ color: 'var(--color-danger)' }}
                >
                  Retirer
                </Button>
              )}
            </div>
          </div>
        ))}

        {members.length === 0 && (
          <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
            Aucun membre pour le moment.
          </p>
        )}
      </div>

      {/* Invite form */}
      {isAdmin && (
        <>
          <h3
            style={{
              fontSize: 'var(--font-size-md)',
              fontWeight: 600,
              marginBottom: 'var(--spacing-sm)',
              color: 'var(--color-text)',
            }}
          >
            Inviter un membre
          </h3>

          <form
            onSubmit={handleInvite}
            style={{
              display: 'flex',
              gap: 'var(--spacing-sm)',
              alignItems: 'flex-end',
            }}
          >
            <div style={{ flex: 1 }}>
              <Input
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="utilisateur@exemple.com"
                required
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-xs)' }}>
              <label
                style={{
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 500,
                  color: 'var(--color-text)',
                }}
              >
                Role
              </label>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value as Role)}
                style={{
                  padding: '8px 12px',
                  backgroundColor: 'var(--color-background)',
                  color: 'var(--color-text)',
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-md)',
                  outline: 'none',
                  cursor: 'pointer',
                }}
              >
                <option value={Role.Admin}>Admin</option>
                <option value={Role.Editor}>Editeur</option>
                <option value={Role.Reader}>Lecteur</option>
              </select>
            </div>

            <Button type="submit" isLoading={isInviting}>
              Inviter
            </Button>
          </form>

          {inviteError && (
            <p
              style={{
                color: 'var(--color-danger)',
                fontSize: 'var(--font-size-sm)',
                marginTop: 'var(--spacing-sm)',
              }}
            >
              {inviteError}
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default MembersPanel;
